import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';
import { apiClient } from '@/lib/api-client';

interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
  /** Cognito groups parsed from the ID token. */
  groups: string[];
  /** True when the user is in the `Admins` Cognito group. */
  isAdmin: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => {},
  refreshSession: async () => {},
});

/** Parse the `cognito:groups` claim from a decoded ID token payload. */
function readGroups(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  const raw = (payload as Record<string, unknown>)['cognito:groups'];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t.startsWith('[') && t.endsWith(']')) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        return t.slice(1, -1).split(',').map((s) => s.trim()).filter(Boolean);
      }
    }
    return [t];
  }
  return [];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        // No valid token -- treat as unauthenticated.
        setUser(null);
        apiClient.setTokenProvider(null);
        setIsLoading(false);
        return;
      }

      // Hand the api-client a token provider rather than a static token.
      // Amplify's `fetchAuthSession()` reads cached tokens and silently
      // refreshes when within the refresh window, so calling it on every
      // API request is cheap and keeps the Authorization header current.
      apiClient.setTokenProvider(async ({ forceRefresh } = {}) => {
        const s = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
        return s.tokens?.idToken?.toString() ?? null;
      });

      const claims = session.tokens?.idToken?.payload;
      const groups = readGroups(claims);

      setUser({
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId || currentUser.username,
        displayName: currentUser.signInDetails?.loginId?.split('@')[0] || currentUser.username,
        groups,
        isAdmin: groups.includes('Admins'),
      });
    } catch {
      setUser(null);
      apiClient.setTokenProvider(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const handleSignOut = useCallback(async () => {
    // Clear React state immediately
    setUser(null);
    apiClient.setTokenProvider(null);

    // Sign out from Cognito
    try {
      await amplifySignOut({ global: true });
    } catch {
      try { await amplifySignOut(); } catch { /* ignore */ }
    }

    // Clear all Amplify/Cognito localStorage keys to prevent stale sessions
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('CognitoIdentityServiceProvider') || key.startsWith('amplify'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch { /* ignore */ }

    // Full page navigation to login -- clears all in-memory state
    window.location.replace('/login');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        signOut: handleSignOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
